import { t } from "@/utils/i18n";
import {
  validateBearerToken,
  validateHeaderKey,
  validateQueryKey,
  validateUrl,
} from "@/utils/rules";
import turndownService from "@/utils/turndownService";
import { findRing } from "@/utils/xgraph-util";
import { Graph, GraphData, INode } from "@antv/g6/es";
import {
  some,
  trim,
  find,
  compact,
  map,
  every,
  entries,
  groupBy,
  get,
} from "lodash-es";
import {
  BranchMode,
  ConditionJudgeData,
  ConditionMode,
  hasEmptyInfoOrVariable,
  hasInfoOrVariable,
  MatchMode,
} from "@/types/conditionJudgeNodeType";
import { AskMode, InfoExtractNodeData } from "@/types/infoExtractNodeType";
import { ReplyMode, ReplyType } from "@/types/endReplyNodeType";
import { FALLBACK_ERROR } from "@/components/const";
import { hasIncompleteEndReplyVariables } from "@/components/agent-node/end-reply-variable-content";

interface NodeData extends ConditionJudgeData, InfoExtractNodeData {
  nodeType: string;
  nodeName?: string;
  intentions?: string;
  type?: string;
  content?: string;
  routePolicy?: string;
  groupId?: string;
  inputVariables?: Array<Record<string, any>>;
  url?: string;
  request?: {
    body?: Array<{
      varType: number; // 1=系统变量, 2=自定义变量, 3=固定值, 5=启动节点变量
      key: string;
      value?: string;
      valueType?: string;
    }>;
  };
  tip?: string;
  desc?: string;
  mode?: ReplyMode;
  examples?: Array<string>;
  optionType?: number;
  fallbackSwitch?: number;
  newTaskID?: number;
  triggerMethod?: number;
  eventId?: string;
  eventTriggerConfig?: {
    eventId?: number | string;
  };
  // 标准任务流格式：嵌套的 auth 对象
  auth?: {
    authType: number;
    bearerAuth: {
      bearerToken: string;
    };
    customAuth: {
      key: string;
      value: string;
    };
  };
  // 智能任务流格式：扁平化的鉴权字段
  authType?: number;
  basicAuth?: {
    basicToken?: string;
  };
  bearerAuth?: {
    bearerToken?: string;
  };
  customAuth?: {
    key?: string;
    value?: string;
  };
  // 接口调用节点的参数和返回值（智能任务流）
  params?: Array<{
    type: number; // 1=信息收集变量, 2=系统变量, 3=自定义变量, 4=固定变量, 5=启动节点变量
    key: string;
    value?: string;
  }>;
  returns?: Array<{
    variable_id: string; // userdefine-xxx格式
    key: string; // 变量名
  }>;
  // 超时时间（毫秒），默认4500，仅智能任务流支持
  callbackTimeout?: number;
}

interface CFG {
  illicitRingType?: Array<string>;
}

type ErrorData =
  | Array<{
      type: string;
      value: string;
    }>
  | undefined;

const concatErrorData = <T>(arr: Array<T> | undefined, newV: T) => {
  return [newV];
};

const hasSelectedTriggerEvent = (nodeData: NodeData) => {
  const eventId = nodeData.eventTriggerConfig?.eventId ?? nodeData.eventId;
  return !!trim(String(eventId ?? ""));
};

const validateStartInputVariables = (inputVariables: Array<Record<string, any>>) => {
  if (!inputVariables.length) {
    return false;
  }

  const varNameRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  if (some(inputVariables, (v) => v.name && !varNameRegex.test(v.name))) {
    return t("格式不正确");
  }

  const names = inputVariables
    .map((v) => v.name)
    .filter((n) => n && trim(n));
  if (new Set(names).size !== names.length) {
    return t("参数名不能重复");
  }

  return false;
};

const validateNodeOutEdgesErrorFn = (
  nodeData: NodeData,
  node: INode,
  xGraph?: Graph,
) => {
  const nodeType = nodeData.nodeType;
  if (["replyMessageNode"].includes(nodeType)) {
    const outEdges = node.getOutEdges();
    if (outEdges.length > 1) {
      outEdges.forEach((edge) => {
        edge.setState("error", true);
      });
      return t("回复节点只能有一个出口连线");
    }
  }

  if (["formSaveNode"].includes(nodeType)) {
    const outEdges = node.getOutEdges();
    if (outEdges.length > 1) {
      outEdges.forEach((edge) => {
        edge.setState("error", true);
      });
      return t("信息收集节点只能有一个出口连线");
    }
  }

  if (["interfaceInvocationNode"].includes(nodeType)) {
    const fallbackSwitch = nodeData.fallbackSwitch;
    const outEdges = node.getOutEdges();

    // 判断是否为智能任务流：检查图中是否存在 userIntentNode 节点
    const isAgentTaskFlow = xGraph
      ? !!xGraph.find("node", (n) => {
          const model = n.getModel();
          return get(model, "nodeData.nodeType") === "userIntentNode";
        })
      : false;

    if (isAgentTaskFlow) {
      // 智能任务流：使用新格式，通过 branchId -> branches[].type 来判断异常处理分支
      const branches = nodeData.branches || [];
      const hasFallbackBranch = outEdges.find((edge) => {
        const model: any = edge.getModel();
        // 优先使用新格式：通过 branchId 查找对应的 branch（支持 branchId 直接属性和 edgeData.branchId）
        const branchId = model.branchId || model.edgeData?.branchId;
        if (branchId && branches.length > 0) {
          const branch = branches.find((b) => b.id === branchId);
          if (branch && branch.type === FALLBACK_ERROR) {
            return true;
          }
        }
        return false;
      });

      if (fallbackSwitch && !hasFallbackBranch) {
        outEdges.forEach((edge) => {
          edge.setState("error", true);
        });
        return t("未配置异常处理节点");
      }
    } else {
      // 标准任务流：使用旧格式，通过 isFallback 字段判断
      const isFallback = outEdges.find((edge) => {
        const model = edge.getModel();
        return !!model.isFallback;
      });
      if (fallbackSwitch && !isFallback) {
        outEdges.forEach((edge) => {
          edge.setState("error", true);
        });
        return t("未配置异常处理节点");
      }
    }
  }

  if (
    [
      "branchNode",
      "conditionalBranchNode",
      "interfaceInvocationNode",
      "transToHumanNode",
      "transToTaskNode",
    ].includes(nodeType)
  ) {
    const outEdges = node.getOutEdges();
    const groupedEdges = entries(
      groupBy(outEdges, (edge) => {
        const model: any = edge.getModel();
        // 支持新格式（branchId 直接属性）和旧格式（edgeData.branchId）
        return model.branchId || model.edgeData?.branchId;
      }),
    );
    let isError = false;
    groupedEdges.forEach(([key, arr]) => {
      // 验证出口连线， branchId 不能重复
      if (key && arr.length > 1) {
        arr.forEach((edge) => {
          edge.setState("error", true);
        });
        isError = true;
      }
    });
    if (isError) {
      return t("存在重复的条件出口");
    }
  }
};

/**
 * @description
 * 仅仅验证数据填写
 * 验证通过返回 undefined | null
 * 不通过，返回 true 或者其他字符串
 */
const validateNodeErrorFn = (
  nodeData: NodeData,
  node: INode,
  fieldValidateCfg?: { maxAnswerLength: number },
  xGraph?: Graph,
) => {
  const {
    branches = [],
    intentions = [],
    triggerMethod = 0,
    eventId = "",
    nodeType,
    newTaskID = "",
    nodeName = "",
    type = "",
    content = "",
    routePolicy = "",
    groupId = "",
    inputVariables = [],
    url = "",
    request,
    tip = "",
    desc = "",
    infos = [],
    examples = [],
    optionType,
    fallbackSwitch = 0,
    matchMode = MatchMode.AI,
    mode = ReplyMode.KNOWLEDGE,
    auth,
    // 智能任务流的扁平化鉴权字段
    authType: flatAuthType,
    basicAuth,
    bearerAuth: flatBearerAuth,
    customAuth: flatCustomAuth,
    callbackTimeout,
  } = nodeData;
  if (!trim(nodeName)) {
    return true;
  }
  if (trim(nodeName).length > 100) {
    return true;
  }
  if (nodeType === "startNode") {
    if (triggerMethod === 0) {
      if (intentions.length === 0) {
        return true;
      }
      if (intentions.length > 0) {
        if (some(intentions, (v) => !trim(v) || trim(v).length > 200)) {
          return true;
        }
      }
      const _arr = compact(intentions);
      if (new Set(_arr).size !== _arr.length) {
        return true;
      }
    }
    if (triggerMethod === 1 && !hasSelectedTriggerEvent(nodeData)) {
      return true;
    }
    const inputVariableError = validateStartInputVariables(inputVariables);
    if (inputVariableError) {
      return inputVariableError;
    }
  }
  if (nodeType === "replyMessageNode") {
    const maxAnswerLength = fieldValidateCfg?.maxAnswerLength ?? 2000;
    const _content =
      type === "2" ? turndownService.turndown(trim(content)) : trim(content);
    if (!_content || _content.length > maxAnswerLength) {
      return true;
    }
  }
  if (nodeType === "branchNode") {
    if (!trim(tip)) {
      return true;
    }
    if (branches.length === 0) {
      return true;
    }
    if (branches.length > 0) {
      const isContentError = find(
        branches,
        (v) => !trim(v.content) || trim(v.content).length > 200,
      );
      if (isContentError) {
        return true;
      }
      if (optionType === 1) {
        const isUrlError = find(
          branches,
          (v) => v.urlStatus && !validateUrl(v.url),
        );
        if (isUrlError) {
          return true;
        }
      }
    }
    // 问法不能重复
    const _arr = map(compact(branches), "content");
    if (new Set(_arr).size !== _arr.length) {
      return true;
    }
  }
  if (nodeType === "conditionalBranchNode") {
    if (branches.length === 0) {
      return true;
    }
    if (branches.length > 0) {
      const isValueOrKeyEmpty = find(branches, (branch) => {
        return find(branch.condition, (v) => {
          if (!v.variableKey) {
            return true;
          }
          return every([
            !["not_exist", "exist"].includes(v.relation),
            !trim(v.value) || trim(v.value).length > 100,
          ]);
        });
      });
      if (isValueOrKeyEmpty) {
        return true;
      }
    }
  }
  if (nodeType === "transToHumanNode") {
    if (!routePolicy) {
      return true;
    }
    if (routePolicy === "1" && !groupId) {
      return true;
    }
  }
  if (nodeType === "formSaveNode") {
    if (!trim(tip)) {
      return true;
    }
    if (!inputVariables.length) {
      return true;
    }
    if (inputVariables.length) {
      return find(inputVariables, (v) => {
        if (!trim(v.name)) {
          return true;
        }
        // 检查 variableKey 是否为空或不是字符串类型
        // if (
        //   !v.variableKey ||
        //   typeof v.variableKey !== "string" ||
        //   !trim(v.variableKey)
        // ) {
        //   return true;
        // }
        // 选项
        if (v.formType === 1) {
          return some(v.chooseItemList, (item) => !trim(item));
        }
        if (v.formType === 0) {
          return !trim(v.placeholder);
        }
      });
    }
  }
  if (nodeType === "interfaceInvocationNode") {
    if (!trim(url)) {
      return true;
    }
    if (!validateUrl(url)) {
      return true;
    }
    if (fallbackSwitch && !find(branches, (v) => v.type === FALLBACK_ERROR)) {
      return true;
    }

    // 判断是否为智能任务流：检查图中是否存在 userIntentNode 节点
    const isAgentTaskFlow = xGraph
      ? !!xGraph.find("node", (n) => {
          const model = n.getModel();
          return get(model, "nodeData.nodeType") === "userIntentNode";
        })
      : false;

    // 标准任务流：使用 auth 对象中的字段（保持原有逻辑）
    // 智能任务流：使用扁平化的字段
    if (isAgentTaskFlow) {
      // 智能任务流的验证逻辑
      // 验证超时时间（仅智能任务流支持）
      if (callbackTimeout !== undefined && callbackTimeout !== null) {
        const numValue = Number(callbackTimeout);
        if (isNaN(numValue) || numValue <= 0) {
          return t("超时时间必须大于0");
        }
        if (numValue > 30000) {
          return t("超时时间不能超过30秒（30000毫秒）");
        }
      }
      if (flatAuthType) {
        // Basic Auth (仅智能任务流支持)
        if (
          every([
            flatAuthType === 1,
            some([
              !trim(basicAuth?.basicToken),
              // Basic Auth token 格式验证可以复用 Bearer token 的验证规则
              !validateBearerToken(basicAuth?.basicToken || ""),
            ]),
          ])
        ) {
          return true;
        }
        // Bearer
        if (
          every([
            flatAuthType === 2,
            some([
              !trim(flatBearerAuth?.bearerToken),
              !validateBearerToken(flatBearerAuth?.bearerToken || ""),
            ]),
          ])
        ) {
          return true;
        }
        // 自定义 Header
        if (
          every([
            flatAuthType === 3,
            some([
              !trim(flatCustomAuth?.key),
              !validateHeaderKey(flatCustomAuth?.key || ""),
            ]),
          ])
        ) {
          return true;
        }
        // 自定义 Query
        if (
          every([
            flatAuthType === 4,
            some([
              !trim(flatCustomAuth?.key),
              !validateQueryKey(flatCustomAuth?.key || ""),
            ]),
          ])
        ) {
          return true;
        }
        // 自定义鉴权的 value 字段验证（仅智能任务流需要验证 value）
        if ([3, 4].includes(flatAuthType) && !trim(flatCustomAuth?.value)) {
          return true;
        }
      }

      const hasIncompleteParam = (nodeData.params || []).some((param) => {
        if (![1, 2, 3, 4, 5].includes(param.type)) {
          return false;
        }

        return !trim(param.key ?? "") || !trim(param.value ?? "");
      });

      if (hasIncompleteParam) {
        return t("存在已失效的变量引用，请重新选择");
      }

    } else {
      // 标准任务流的验证逻辑（保持原有逻辑不变）
      const startNodeInputVariableNames = new Set<string>();
      if (xGraph) {
        xGraph.getNodes().forEach((graphNode) => {
          const model = graphNode.getModel();
          const data = model.nodeData as NodeData;
          if (data.nodeType === "startNode" && data.inputVariables?.length) {
            data.inputVariables.forEach((item) => {
              const name = trim(item.name ?? "");
              if (name) {
                startNodeInputVariableNames.add(name);
              }
            });
          }
        });
      }

      const hasInvalidStartNodeVariable = request?.body?.some((param) => {
        if (param.varType !== 5) {
          return false;
        }
        const key = trim(param.key ?? "");
        if (!key) {
          return false;
        }
        return !startNodeInputVariableNames.has(key);
      });

      if (hasInvalidStartNodeVariable) {
        return t("存在已失效的启动节点变量，请重新选择");
      }

      if (auth?.authType) {
        const { authType, bearerAuth, customAuth } = auth;
        // Bearer
        if (
          every([
            authType === 2,
            some([
              !trim(bearerAuth.bearerToken),
              !validateBearerToken(bearerAuth.bearerToken),
            ]),
          ])
        ) {
          return true;
        }
        // 自定义 Header
        if (
          every([
            authType === 3,
            some([!trim(customAuth.key), !validateHeaderKey(customAuth.key)]),
          ])
        ) {
          return true;
        }
        // 自定义 Query
        if (
          every([
            authType === 4,
            some([!trim(customAuth.key), !validateQueryKey(customAuth.key)]),
          ])
        ) {
          return true;
        }
      }
    }
  }
  if (nodeType === "transToTaskNode") {
    if (!newTaskID) {
      return true;
    }
  }

  if (nodeType === "userIntentNode") {
    if (!trim(desc)) {
      return true;
    }
    if (desc.length > 1000) {
      return true;
    }
    if (examples.length) {
      const uniqueExamples = compact(examples);
      const hasDuplicates =
        new Set(uniqueExamples).size !== uniqueExamples.length;
      if (hasDuplicates) {
        return true;
      }
    }
    if (triggerMethod === 1 && !hasSelectedTriggerEvent(nodeData)) {
      return true;
    }
    const inputVariableError = validateStartInputVariables(inputVariables);
    if (inputVariableError) {
      return inputVariableError;
    }
  }
  if (nodeType === "infoExtractNode") {
    if (infos.length > 10) {
      return true;
    }
    // 信息内容：必填，标题长度[1,50]字符；
    // 信息描述：非必填，最长 1000 字符；
    // 支持配置快捷选项（非必填）。选项数量[1,20]，单个选项长度[1,200]字符
    const isInfoError = find(infos, (info) => {
      let isError = false;
      if (!trim(info.name)) {
        isError = true;
      }
      if (info.desc?.length > 1000) {
        isError = true;
      }
      // 固定话术询问
      if (info.askMode === AskMode.FIXED) {
        // 快捷选项内容：必填，最长 200 字符；
        if (
          !trim(info?.fix_ask?.content) ||
          trim(info?.fix_ask?.content).length > 200
        ) {
          isError = true;
        }
        // 快捷选项数量[0,20]
        if ((info?.fix_ask?.items?.length ?? 0) > 20) {
          isError = true;
        }
        // 快捷选项长度[0,200]
        if (info?.fix_ask?.items?.find((v) => trim(v).length > 200)) {
          isError = true;
        }
      }
      return isError;
    });
    if (isInfoError) {
      return true;
    }
  }
  if (nodeType === "conditionJudgeNode") {
    if (matchMode === MatchMode.AI) {
      // 需要校验添加的条件中是否包含信息或变量。
      let errorTip = "";
      const isBranchError = find(
        branches.filter((v) => v.mode !== BranchMode.ELSE),
        (branch) => {
          return find(branch.conditions, (v) => {
            if (!hasInfoOrVariable(v.content)) {
              errorTip = t("条件中必须包含信息或变量");
            }
            if (hasEmptyInfoOrVariable(v.content)) {
              errorTip = t("存在未选择的信息或变量");
            }
            if ((branch.originContent?.length ?? 0) > 200) {
              errorTip = t("条件内容不能超过200个字符");
            }
            return errorTip !== "";
          });
        },
      );
      if (isBranchError) {
        return errorTip;
      }
    }
    if (matchMode === MatchMode.RULE) {
      if (branches.length === 0) {
        return true;
      }
      if (branches.length > 0) {
        const filterElseBranch = branches.filter(
          (v) => v.mode !== BranchMode.ELSE,
        );
        const isValueOrContentEmpty = find(filterElseBranch, (branch) => {
          return find(branch.conditions, (v) => {
            if (!v.info_id) {
              return true;
            }
            // 只有当关系不是"为空"或"不为空"时，才需要检查 content
            if (
              ![ConditionMode.IS_NULL, ConditionMode.NOT_NULL].includes(
                v.mode,
              ) &&
              !v.content
            ) {
              return true;
            }
            return false;
          });
        });
        if (isValueOrContentEmpty) {
          return true;
        }
      }
    }
  }
  if (nodeType === "endReplyNode") {
    if (mode === ReplyMode.FIXED) {
      const maxAnswerLength = fieldValidateCfg?.maxAnswerLength ?? 2000;
      const _content =
        type === ReplyType.RICH_TEXT
          ? turndownService.turndown(trim(content))
          : trim(content);
      if (hasIncompleteEndReplyVariables(_content)) {
        return t("存在未选择的信息或变量");
      }
      if (!_content || _content.length > maxAnswerLength) {
        return true;
      }
    }
  }
};

/**
 * @description 只验证某个节点
 */
export const validateNode = (node: INode, fieldValidateCfg, xGraph?: Graph) => {
  node.setState("error", false);
  const nodeModel = node.getModel();
  const nodeData = nodeModel.nodeData as NodeData;
  const errorData = (nodeModel.errorData || []) as ErrorData;
  const nodeError = validateNodeErrorFn(
    nodeData,
    node,
    fieldValidateCfg,
    xGraph,
  );
  if (nodeError) {
    node.setState("error", true);
    node.update({
      errorData: concatErrorData(errorData, {
        type: "dataError",
        value:
          typeof nodeError === "string" ? nodeError : t("节点数据填写不正确"),
      }),
    });
  }
  const nOEError = validateNodeOutEdgesErrorFn(nodeData, node, xGraph);
  if (nOEError) {
    node.setState("error", true);
    node.update({
      errorData: concatErrorData(errorData, {
        type: "dataError",
        value: typeof nOEError === "string" ? nOEError : t("节点连线不正确"),
      }),
    });
  }
};

const validateNodeDataStep = async (xGraph: Graph, fieldValidateCfg) => {
  const graphNodes = xGraph.getNodes() || [];
  for (const node of graphNodes) {
    const nodeModel = node.getModel();
    const nodeData = nodeModel.nodeData as NodeData;
    const errorData = (nodeModel.errorData || []) as ErrorData;

    const nodeError = validateNodeErrorFn(
      nodeData,
      node,
      fieldValidateCfg,
      xGraph,
    );
    if (nodeError) {
      node.setState("error", true);
      node.update({
        errorData: concatErrorData(errorData, {
          type: "dataError",
          value:
            typeof nodeError === "string" ? nodeError : t("节点数据填写不正确"),
        }),
      });
    }
    const nOEError = validateNodeOutEdgesErrorFn(nodeData, node, xGraph);
    if (nOEError) {
      node.setState("error", true);
      node.update({
        errorData: concatErrorData(errorData, {
          type: "dataError",
          value: typeof nOEError === "string" ? nOEError : t("节点连线不正确"),
        }),
      });
    }
  }
};

const validateRingList = async (xGraph: Graph, cfg: CFG) => {
  const illicitRingType = cfg.illicitRingType || [];

  if (illicitRingType.length > 0) {
    const xGraphData = xGraph.save();
    const ringList = findRing(xGraphData as GraphData);

    const graphNodes = xGraph.getNodes() || [];
    const graphEdges = xGraph.getEdges() || [];

    ringList.forEach((v) => {
      const { nodes, edges } = v;
      const nodeIds = nodes.map((v) => v.id);
      const isError = nodes.every((v) => {
        const type = get(v, "nodeData.nodeType", "");
        return illicitRingType.includes(type);
      });
      if (isError) {
        const errorGraphNodes = graphNodes.filter((v) => {
          return nodeIds.includes(v.get("id"));
        });

        errorGraphNodes.forEach((node) => {
          const nodeModel = node.getModel();
          const errorData = (nodeModel.errorData || []) as ErrorData;
          node.setState("error", true);
          node.update({
            errorData: concatErrorData(errorData, {
              type: "ringError",
              value: t("节点存在回环，不允许自动节点回环"),
            }),
          });
        });

        const edgeIds = edges.map((v) => v.id);
        const errorGraphEdges = graphEdges.filter((v) => {
          return edgeIds.includes(v.get("id"));
        });

        errorGraphEdges.forEach((edge) => {
          edge.setState("error", true);
        });
      }
    });
  }
};

/**
 * @description 验证节点在开始节点的树中
 */
const validateNodeIsInTree = async (xGraph: Graph) => {
  const graphNodes = xGraph.getNodes() || [];
  const graphEdges = xGraph.getEdges() || [];

  const startNode = xGraph.find("node", (v) => {
    return v.getModel()["type"] === "start-node";
  });

  const allEdges: any[] = [];
  const allNodes: any[] = [];
  let nodes = [startNode];
  let edgesArr: any[] = [];

  // 最大 2000 层 嵌套子元素，再大计算可能会很久。放弃计算。
  for (let i = 0; i < 2000; i++) {
    for (const node of nodes) {
      if (allNodes.includes(node)) continue;
      allNodes.push(node);
      if (node && node.get("type") === "node") {
        const cEdges = (node as INode).getOutEdges();
        edgesArr.push(cEdges);
      }
    }
    if (edgesArr.length === 0) break;

    nodes = [];
    for (const edges of edgesArr) {
      for (const edge of edges) {
        if (allEdges.includes(edge)) continue;
        allEdges.push(edge);
        nodes.push(edge.getTarget());
      }
    }
    edgesArr = [];
    if (nodes.length === 0) break;
  }

  const diffNodes = graphNodes.filter((node) => {
    return !allNodes.includes(node);
  });
  const diffEdges = graphEdges.filter((node) => {
    return !allEdges.includes(node);
  });

  diffNodes.forEach((node) => {
    node.setState("error", true);
    const nodeModel = node.getModel();
    const errorData = (nodeModel.errorData || []) as ErrorData;
    node.update({
      errorData: concatErrorData(errorData, {
        type: "inTreeError",
        value: t("节点没有连接入主路"),
      }),
    });
  });

  diffEdges.forEach((edge) => {
    edge.setState("error", true);
  });
};

export const validateXGraph = async (
  xGraph: Graph,
  cfg: CFG,
  fieldValidateCfg,
) => {
  console.log("========= 执行一次全画板验证 ==========");

  try {
    const graphNodes = xGraph.getNodes() || [];
    const graphEdges = xGraph.getEdges() || [];

    graphNodes.forEach((node) => {
      node.setState("error", false);
      node.update({
        errorData: [],
      });
    });
    graphEdges.forEach((node) => {
      node.setState("error", false);
    });

    await validateNodeDataStep(xGraph, fieldValidateCfg);
    await validateRingList(xGraph, cfg);

    await validateNodeIsInTree(xGraph);

    const flag = !find(graphNodes, (node) => {
      const states = node.getStates();
      return states.includes("error");
    });
    console.log("========== 全画板验证完成 =========", flag);
    return flag; //flag false 不通过校验
  } catch (error) {
    return false;
  }
};
